import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMarketingOpen, setIsMarketingOpen] = useState(false);
  const [isMobileMarketingOpen, setIsMobileMarketingOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsMarketingOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const marketingLinks = [
    { name: 'All Marketing Services', path: '/digital-marketing' },
    { name: 'SEO', path: '/seo' },
    { name: 'Google Ads (PPC)', path: '/google-ads' },
    { name: 'Social Media', path: '/social-media-marketing' },
    { name: 'Content Marketing', path: '/content-marketing' },
    { name: 'Law Firm Marketing', path: '/law-firm-marketing' },
    { name: 'Medical Practice Marketing', path: '/medical-practice-marketing' },
  ];

  const navLinks = [
    { name: 'Services', path: '/services' },
    { name: 'Marketing', path: '/digital-marketing', hasDropdown: true },
    { name: 'Pricing', path: '/pricing' },
    { name: 'Work', path: '/work' },
    { name: 'Blog', path: '/blog' },
    { name: 'About', path: '/about' },
  ];

  const isActivePath = (path) => location.pathname === path;
  const isMarketingActive = marketingLinks.some(link => location.pathname === link.path);

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-sm shadow-sm' 
          : 'bg-white'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="relative w-16 h-16">
              <img src="/trika-logo.png" alt="Trika Studio Logo" className="w-full h-full object-contain" />
            </div>
            <span className="text-xl font-bold text-[#111010] font-['Space_Grotesk']">
              Trika Studio
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              link.hasDropdown ? (
                <div key={link.path} className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsMarketingOpen(!isMarketingOpen)}
                    className={`text-sm font-medium transition-colors flex items-center gap-1 ${
                      isMarketingActive
                        ? 'text-theme-primary'
                        : 'text-[#111010] hover:text-theme-primary'
                    }`}
                  >
                    {link.name}
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isMarketingOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isMarketingOpen && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-64 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50">
                      {marketingLinks.map((mLink) => (
                        <Link
                          key={mLink.path}
                          to={mLink.path}
                          onClick={() => setIsMarketingOpen(false)}
                          className={`block px-4 py-2.5 text-sm transition-colors ${
                            isActivePath(mLink.path)
                              ? 'text-theme-primary bg-theme-primary/5 font-medium'
                              : 'text-gray-700 hover:text-theme-primary hover:bg-gray-50'
                          }`}
                        >
                          {mLink.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors ${
                    isActivePath(link.path)
                      ? 'text-theme-primary'
                      : 'text-[#111010] hover:text-theme-primary'
                  }`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </div>

          {/* CTA Button and Phone */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Phone Badge */}
            <a 
              href="tel:919-229-5525"
              className="flex items-center space-x-2 px-4 py-2 bg-theme-accent hover:bg-theme-accent/90 text-white rounded-full transition-colors group"
            >
              <Phone className="w-4 h-4" />
              <span className="text-sm font-medium">919-229-5525</span>
            </a>
            <Link to="/contact">
              <Button className="bg-theme-primary hover:bg-theme-primary/90 text-white rounded-full px-6">
                Start a Project
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-[#111010]" />
            ) : (
              <Menu className="w-6 h-6 text-[#111010]" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-[#E8E4DD]">
            <div className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                link.hasDropdown ? (
                  <div key={link.path}>
                    <button
                      onClick={() => setIsMobileMarketingOpen(!isMobileMarketingOpen)}
                      className={`text-sm font-medium transition-colors flex items-center gap-1 ${
                        isMarketingActive ? 'text-theme-primary' : 'text-[#111010]'
                      }`}
                    >
                      {link.name}
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isMobileMarketingOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isMobileMarketingOpen && (
                      <div className="ml-4 mt-2 space-y-2 border-l-2 border-theme-primary/20 pl-4">
                        {marketingLinks.map((mLink) => (
                          <Link
                            key={mLink.path}
                            to={mLink.path}
                            onClick={() => { setIsMobileMenuOpen(false); setIsMobileMarketingOpen(false); }}
                            className={`block text-sm transition-colors ${
                              isActivePath(mLink.path)
                                ? 'text-theme-primary font-medium'
                                : 'text-gray-600 hover:text-theme-primary'
                            }`}
                          >
                            {mLink.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`text-sm font-medium transition-colors ${
                      isActivePath(link.path)
                        ? 'text-theme-primary'
                        : 'text-[#111010] hover:text-theme-primary'
                    }`}
                  >
                    {link.name}
                  </Link>
                )
              ))}
              {/* Phone in Mobile Menu */}
              <a 
                href="tel:919-229-5525"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center justify-center space-x-2 px-4 py-3 bg-theme-accent hover:bg-theme-accent/90 text-white rounded-full transition-colors"
              >
                <Phone className="w-4 h-4" />
                <span className="text-sm font-medium">919-229-5525</span>
              </a>
              <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
                <Button className="w-full bg-theme-primary hover:bg-theme-primary/90 text-white rounded-full">
                  Start a Project
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
